import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Editor from '@monaco-editor/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Play, Sparkles, Send, Loader2, Users, Activity, Code2, Eye, Maximize2, X, ChevronRight, ChevronLeft, Package, AlertCircle, RefreshCw, Shield, Target, TestTube, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { TransportLifecycleTracker } from '@/components/TransportLifecycleTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Design Output View Component
function DesignOutputView({ ticketId, agentTasks }) {
  const designTask = agentTasks.find(t => t.agent_type === 'design');

  if (!designTask) {
    return (
      <div className="h-full flex items-center justify-center text-center p-8">
        <div>
          <Sparkles className="h-16 w-16 mx-auto mb-4 text-purple-300" />
          <p className="text-slate-600 mb-2">No design output yet</p>
          <p className="text-sm text-slate-500">Execute workflow to generate design document</p>
        </div>
      </div>
    );
  }

  const designOutput = designTask.output_data || {};
  const designDocument = typeof designOutput === 'string' ? designOutput : JSON.stringify(designOutput, null, 2);

  return (
    <div className="h-full flex flex-col">
      <div className="bg-purple-50 border-b border-purple-200 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          <span className="font-semibold text-purple-900">Design Agent Output</span>
          <Badge className="bg-purple-600 text-white text-xs">
            {designTask.status === 'completed' ? '✓ Completed' : designTask.status}
          </Badge>
        </div>
        {designTask.completed_at && (
          <span className="text-xs text-purple-700">
            {new Date(designTask.completed_at).toLocaleString()}
          </span>
        )}
      </div>
      
      <div className="flex-1 overflow-hidden">
        <Editor
          height="100%"
          language="json"
          value={designDocument}
          theme="vs-dark"
          options={{
            readOnly: true,
            minimap: { enabled: false },
            fontSize: 12,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true
          }}
        />
      </div>

      {/* Quick Insights */}
      <div className="bg-slate-50 border-t border-slate-200 p-3">
        <div className="grid grid-cols-3 gap-3 text-xs">
          {designOutput.clean_core_compliant !== undefined && (
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-green-600" />
              <span>Clean Core: {designOutput.clean_core_compliant ? 'Yes' : 'No'}</span>
            </div>
          )}
          {designOutput.compliance_score !== undefined && (
            <div className="flex items-center gap-1">
              <Shield className="h-3 w-3 text-blue-600" />
              <span>Compliance: {designOutput.compliance_score}%</span>
            </div>
          )}
          {designOutput.data_model && (
            <div className="flex items-center gap-1">
              <Code2 className="h-3 w-3 text-purple-600" />
              <span>Data Model: Available</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Test Output View Component
function TestOutputView({ ticketId, agentTasks }) {
  const testTask = agentTasks.find(t => t.agent_type === 'test');

  if (!testTask) {
    return (
      <div className="h-full flex items-center justify-center text-center p-8">
        <div>
          <TestTube className="h-16 w-16 mx-auto mb-4 text-amber-300" />
          <p className="text-slate-600 mb-2">No test results yet</p>
          <p className="text-sm text-slate-500">Execute workflow to generate test cases</p>
        </div>
      </div>
    );
  }

  const testOutput = testTask.output_data || {};
  const testDocument = typeof testOutput === 'string' ? testOutput : JSON.stringify(testOutput, null, 2);

  return (
    <div className="h-full flex flex-col">
      <div className="bg-amber-50 border-b border-amber-200 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TestTube className="h-5 w-5 text-amber-600" />
          <span className="font-semibold text-amber-900">Test Agent Output</span>
          <Badge className="bg-amber-600 text-white text-xs">
            {testTask.status === 'completed' ? '✓ Completed' : testTask.status}
          </Badge>
        </div>
        {testTask.completed_at && (
          <span className="text-xs text-amber-700">
            {new Date(testTask.completed_at).toLocaleString()}
          </span>
        )}
      </div>
      
      {/* Test Summary Cards */}
      <div className="bg-white border-b border-slate-200 p-4">
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-3 bg-green-50 rounded">
            <div className="text-2xl font-bold text-green-600">
              {testOutput.tests_passed || 0}
            </div>
            <div className="text-xs text-slate-600 mt-1">Passed</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded">
            <div className="text-2xl font-bold text-red-600">
              {testOutput.tests_failed || 0}
            </div>
            <div className="text-xs text-slate-600 mt-1">Failed</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded">
            <div className="text-2xl font-bold text-blue-600">
              {testOutput.total_tests || 0}
            </div>
            <div className="text-xs text-slate-600 mt-1">Total</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded">
            <div className="text-2xl font-bold text-purple-600">
              {testOutput.coverage_percentage || 0}%
            </div>
            <div className="text-xs text-slate-600 mt-1">Coverage</div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <Editor
          height="100%"
          language="json"
          value={testDocument}
          theme="vs-dark"
          options={{
            readOnly: true,
            minimap: { enabled: false },
            fontSize: 12,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true
          }}
        />
      </div>
    </div>
  );
}

// DevOps Output View Component
function DevOpsOutputView({ ticketId, agentTasks }) {
  const devopsTask = agentTasks.find(t => t.agent_type === 'devops');

  if (!devopsTask) {
    return (
      <div className="h-full flex items-center justify-center text-center p-8">
        <div>
          <Package className="h-16 w-16 mx-auto mb-4 text-orange-300" />
          <p className="text-slate-600 mb-2">No DevOps output yet</p>
          <p className="text-sm text-slate-500">Execute workflow to generate deployment artifacts</p>
        </div>
      </div>
    );
  }

  const devopsOutput = devopsTask.output_data || {};
  const devopsDocument = typeof devopsOutput === 'string' ? devopsOutput : JSON.stringify(devopsOutput, null, 2);

  return (
    <div className="h-full flex flex-col">
      <div className="bg-orange-50 border-b border-orange-200 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-orange-600" />
          <span className="font-semibold text-orange-900">DevOps Agent Output</span>
          <Badge className="bg-orange-600 text-white text-xs">
            {devopsTask.status === 'completed' ? '✓ Completed' : devopsTask.status}
          </Badge>
        </div>
        {devopsTask.completed_at && (
          <span className="text-xs text-orange-700">
            {new Date(devopsTask.completed_at).toLocaleString()}
          </span>
        )}
      </div>

      {/* Transport Info */}
      {devopsOutput.transport_number && (
        <div className="bg-white border-b border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-600 mb-1">Transport Request</div>
              <div className="text-lg font-bold text-orange-600 font-mono">
                {devopsOutput.transport_number}
              </div>
            </div>
            <Badge className="bg-orange-100 text-orange-700">Ready for QA</Badge>
          </div>
        </div>
      )}
      
      <div className="flex-1 overflow-hidden">
        <Editor
          height="100%"
          language="json"
          value={devopsDocument}
          theme="vs-dark"
          options={{
            readOnly: true,
            minimap: { enabled: false },
            fontSize: 12,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true
          }}
        />
      </div>

      {/* Deployment Artifacts */}
      {devopsOutput.artifacts && devopsOutput.artifacts.length > 0 && (
        <div className="bg-slate-50 border-t border-slate-200 p-3">
          <div className="text-xs font-semibold text-slate-700 mb-2">
            Deployment Artifacts ({devopsOutput.artifacts.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {devopsOutput.artifacts.map((artifact, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {artifact}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Intelligent Fiori Preview Component - Generates ticket-specific UI
function FioriPreview({ code, ticket, isVisible }) {
  const [previewHtml, setPreviewHtml] = useState('');

  useEffect(() => {
    const intelligentFioriUI = generateIntelligentFioriUI(ticket, code);
    setPreviewHtml(intelligentFioriUI);
  }, [code, ticket]);

  const generateIntelligentFioriUI = (ticket, code) => {
    const title = ticket?.title || 'SAP Fiori Application';
    const description = ticket?.description || '';
    const hasCode = code && code.trim().length > 20;
    
    // Analyze the ticket and code to determine UI type
    const isExpenseApp = title.toLowerCase().includes('expense') || description.toLowerCase().includes('expense') || 
                         code?.toLowerCase().includes('expense') || code?.toLowerCase().includes('trip');
    const isApprovalWorkflow = description.toLowerCase().includes('approve') || code?.toLowerCase().includes('approve');
    
    // Extract entity names and fields from code if available
    let entities = [];
    let fields = [];
    
    if (code) {
      // Extract entity definitions
      const entityMatches = code.match(/entity\s+(\w+)/g);
      if (entityMatches) {
        entities = entityMatches.map(m => m.replace('entity ', ''));
      }
      
      // Extract field names
      const fieldMatches = code.match(/(\w+)\s*:/g);
      if (fieldMatches) {
        fields = fieldMatches.map(m => m.replace(':', '').trim()).filter(f => 
          !['key', 'type', 'UUID', 'String', 'Date', 'Boolean', 'Integer', 'Decimal'].includes(f)
        );
      }
    }
    
    // Generate ticket-specific Fiori UI
    if (isExpenseApp && hasCode) {
      return generateExpenseFioriUI(title, entities, fields);
    } else if (hasCode) {
      return generateGenericDataFioriUI(title, entities, fields);
    } else {
      return generateEmptyState(title);
    }
  };

  const generateExpenseFioriUI = (title, entities, fields) => {
    return `
      <div style="font-family: '72', Arial, sans-serif; background: #f7f7f7; padding: 0; margin: 0; height: 100%;">
        <!-- Fiori Shell Bar -->
        <div style="background: #354a5f; color: white; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <div style="display: flex; align-items: center; gap: 16px;">
            <div style="width: 32px; height: 32px; background: #5899da; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-weight: bold;">
              S4
            </div>
            <span style="font-size: 16px; font-weight: 600;">${title}</span>
          </div>
          <div style="display: flex; gap: 12px; align-items: center;">
            <button style="background: none; border: none; color: white; cursor: pointer; padding: 8px;">
              <span style="font-size: 20px;">🔔</span>
            </button>
            <div style="width: 32px; height: 32px; background: #7fa8d4; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px;">
              VM
            </div>
          </div>
        </div>

        <!-- Page Header -->
        <div style="background: white; padding: 24px 32px; border-bottom: 1px solid #e5e5e5;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h1 style="margin: 0; font-size: 24px; font-weight: 400; color: #32363a;">My Trip Requests</h1>
              <p style="margin: 8px 0 0 0; color: #6a6d70; font-size: 14px;">Manage expense reimbursement and travel requests</p>
            </div>
            <div style="display: flex; gap: 8px;">
              <button style="background: #0854a0; border: none; color: white; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500;">
                + Create Trip Request
              </button>
            </div>
          </div>
        </div>

        <!-- Filter/Search Bar -->
        <div style="padding: 16px 32px; background: #fafafa; border-bottom: 1px solid #e5e5e5;">
          <div style="display: grid; grid-template-columns: 2fr 1fr 1fr 100px; gap: 12px; align-items: end;">
            <div>
              <label style="display: block; font-size: 12px; color: #6a6d70; margin-bottom: 4px; font-weight: 500;">Search</label>
              <input type="text" placeholder="Search by destination or request ID..." style="width: 100%; padding: 9px 12px; border: 1px solid #d9d9d9; border-radius: 4px; font-size: 14px;">
            </div>
            <div>
              <label style="display: block; font-size: 12px; color: #6a6d70; margin-bottom: 4px; font-weight: 500;">Status</label>
              <select style="width: 100%; padding: 9px 12px; border: 1px solid #d9d9d9; border-radius: 4px; font-size: 14px;">
                <option>All Statuses</option>
                <option>Pending Approval</option>
                <option>Approved</option>
                <option>Posted to Finance</option>
                <option>Rejected</option>
              </select>
            </div>
            <div>
              <label style="display: block; font-size: 12px; color: #6a6d70; margin-bottom: 4px; font-weight: 500;">Date Range</label>
              <input type="date" style="width: 100%; padding: 9px 12px; border: 1px solid #d9d9d9; border-radius: 4px; font-size: 14px;">
            </div>
            <button style="background: #0854a0; border: none; color: white; padding: 10px 16px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500;">
              Go
            </button>
          </div>
        </div>

        <!-- Content Area -->
        <div style="padding: 24px 32px;">
          <!-- Trip Requests Table -->
          <div style="background: white; border-radius: 4px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); overflow: hidden;">
            <!-- Table Header -->
            <div style="border-bottom: 2px solid #e5e5e5; background: #fafafa;">
              <div style="display: grid; grid-template-columns: 120px 2fr 1fr 1fr 1fr 1fr 100px; padding: 14px 16px; font-size: 13px; font-weight: 600; color: #32363a;">
                <div>Request ID</div>
                <div>Destination / Purpose</div>
                <div>Travel Dates</div>
                <div>Amount</div>
                <div>Status</div>
                <div>Manager</div>
                <div>Actions</div>
              </div>
            </div>
            
            <!-- Table Rows - EXPENSE SPECIFIC DATA -->
            ${generateExpenseRows()}
          </div>
        </div>
      </div>
    `;
  };

  const generateExpenseRows = () => {
    const rows = [
      {
        id: 'TR-2025-001',
        destination: 'New York, NY',
        purpose: 'Client Meeting',
        startDate: '2025-11-15',
        endDate: '2025-11-17',
        amount: '$1,245.00',
        currency: 'USD',
        status: 'Pending Approval',
        statusColor: '#d28500',
        manager: 'John Smith'
      },
      {
        id: 'TR-2025-002',
        destination: 'London, UK',
        purpose: 'SAP Conference',
        startDate: '2025-12-01',
        endDate: '2025-12-05',
        amount: '£2,350.00',
        currency: 'GBP',
        status: 'Approved',
        statusColor: '#2b7c2b',
        manager: 'Sarah Wilson'
      },
      {
        id: 'TR-2025-003',
        destination: 'Berlin, Germany',
        purpose: 'Training Workshop',
        startDate: '2025-11-20',
        endDate: '2025-11-22',
        amount: '€890.00',
        currency: 'EUR',
        status: 'Posted to Finance',
        statusColor: '#0854a0',
        manager: 'Mike Johnson'
      },
      {
        id: 'TR-2025-004',
        destination: 'Tokyo, Japan',
        purpose: 'Partner Meeting',
        startDate: '2025-12-10',
        endDate: '2025-12-14',
        amount: '¥345,000',
        currency: 'JPY',
        status: 'Draft',
        statusColor: '#6a6d70',
        manager: 'Unassigned'
      }
    ];
    
    return rows.map(row => `
      <div style="display: grid; grid-template-columns: 120px 2fr 1fr 1fr 1fr 1fr 100px; padding: 16px; border-bottom: 1px solid #f7f7f7; align-items: center; font-size: 14px; color: #32363a; transition: background 0.2s;" onmouseover="this.style.background='#f7f7f7'" onmouseout="this.style.background='white'">
        <div style="font-weight: 500; color: #0854a0;">${row.id}</div>
        <div>
          <div style="font-weight: 500; margin-bottom: 4px;">${row.destination}</div>
          <div style="font-size: 12px; color: #6a6d70;">${row.purpose}</div>
        </div>
        <div style="font-size: 13px;">
          <div>${row.startDate}</div>
          <div style="color: #6a6d70; font-size: 12px;">to ${row.endDate}</div>
        </div>
        <div style="font-weight: 500;">${row.amount}</div>
        <div>
          <span style="display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; background: ${row.statusColor}20; color: ${row.statusColor};">
            ${row.status}
          </span>
        </div>
        <div style="font-size: 13px;">${row.manager}</div>
        <div style="display: flex; gap: 4px;">
          ${row.status === 'Pending Approval' ? `
            <button style="background: #2b7c2b; border: none; color: white; padding: 6px 10px; border-radius: 3px; cursor: pointer; font-size: 12px;">
              Approve
            </button>
          ` : ''}
          ${row.status === 'Approved' ? `
            <button style="background: #0854a0; border: none; color: white; padding: 6px 10px; border-radius: 3px; cursor: pointer; font-size: 12px;">
              Post
            </button>
          ` : ''}
          <button style="background: white; border: 1px solid #d9d9d9; color: #32363a; padding: 6px 10px; border-radius: 3px; cursor: pointer; font-size: 12px;">
            View
          </button>
        </div>
      </div>
    `).join('');
  };

  const generateGenericDataFioriUI = (title, entities, fields) => {
    const displayEntity = entities[0] || 'Data';
    const displayFields = fields.slice(0, 5) || ['ID', 'Name', 'Status', 'Date', 'Amount'];
    
    return `
      <div style="font-family: '72', Arial, sans-serif; background: #f7f7f7; padding: 0; margin: 0; height: 100%;">
        <!-- Fiori Shell Bar -->
        <div style="background: #354a5f; color: white; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <div style="display: flex; align-items: center; gap: 16px;">
            <div style="width: 32px; height: 32px; background: #5899da; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-weight: bold;">
              S4
            </div>
            <span style="font-size: 16px; font-weight: 600;">${title}</span>
          </div>
          <div style="display: flex; gap: 12px; align-items: center;">
            <div style="width: 32px; height: 32px; background: #7fa8d4; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px;">
              VM
            </div>
          </div>
        </div>

        <!-- Page Header -->
        <div style="background: white; padding: 24px 32px; border-bottom: 1px solid #e5e5e5;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h1 style="margin: 0; font-size: 24px; font-weight: 400; color: #32363a;">Manage ${displayEntity}</h1>
              <p style="margin: 8px 0 0 0; color: #6a6d70; font-size: 14px;">Custom ${displayEntity} Application</p>
            </div>
            <button style="background: #0854a0; border: none; color: white; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-size: 14px;">
              + Create
            </button>
          </div>
        </div>

        <!-- Content -->
        <div style="padding: 24px 32px;">
          <div style="background: white; border-radius: 4px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); padding: 20px;">
            <div style="text-sm font-semibold mb-4 color: #32363a;">Generated from entities: ${entities.join(', ')}</div>
            <div style="color: #6a6d70;">Fields detected: ${displayFields.join(', ')}</div>
          </div>
        </div>
      </div>
    `;
  };

  const generateEmptyState = (title) => {
    return `
      <div style="font-family: '72', Arial, sans-serif; background: #f7f7f7; padding: 0; margin: 0; height: 100%; display: flex; align-items: center; justify-content: center;">
        <div style="text-align: center; padding: 48px;">
          <div style="font-size: 64px; margin-bottom: 16px; opacity: 0.3;">📱</div>
          <h3 style="color: #32363a; font-size: 18px; font-weight: 400; margin: 0 0 8px 0;">Waiting for Code Generation</h3>
          <p style="color: #6a6d70; font-size: 14px; margin: 0;">Execute workflow to generate Fiori UI preview</p>
        </div>
      </div>
    `;
  };

  if (!isVisible) return null;

  return (
    <div className="h-full w-full overflow-auto border-l bg-white">
      <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-indigo-50 p-3 border-b flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-blue-600" />
          <span className="font-semibold text-sm">Fiori Interface Preview</span>
        </div>
        <Badge variant="outline" className="text-xs">Live Simulation</Badge>
      </div>
      <iframe
        srcDoc={previewHtml}
        style={{ width: '100%', height: 'calc(100% - 60px)', border: 'none' }}
        title="Fiori Preview"
        sandbox="allow-same-origin"
      />
    </div>
  );
}

// Component to render error resolution plan
function ErrorResolutionPlan({ plan, onExecute, executing }) {
  if (!plan) return null;

  return (
    <div className="p-4 border-2 border-blue-500 rounded-lg bg-blue-50">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-lg">📋 Error Resolution Plan</div>
        <div className="flex gap-2">
          <Badge variant={plan.source === 'learned_knowledge' ? 'default' : 'outline'}>
            {plan.source === 'learned_knowledge' ? '🎓 Learned Resolution' : '🤖 AI Generated'}
          </Badge>
          <Badge className="bg-blue-600">
            {(plan.confidence * 100).toFixed(0)}% Confidence
          </Badge>
        </div>
      </div>

      {plan.source === 'learned_knowledge' && (
        <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-sm">
          ✅ This resolution has been used <strong>{plan.times_used} times</strong> with <strong>{plan.success_rate.toFixed(0)}% success rate</strong>
        </div>
      )}

      <div className="mb-3">
        <div className="text-sm font-semibold mb-2">Resolution Steps:</div>
        <div className="space-y-2">
          {plan.steps.map((step, idx) => (
            <div key={idx} className="flex gap-2 p-3 bg-white border rounded-lg">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                {idx + 1}
              </div>
              <div className="flex-1">
                <div className="text-sm">{step}</div>
                {plan.detailed_resolution?.resolution_steps?.[idx]?.transaction_code && (
                  <div className="text-xs text-muted-foreground mt-1">
                    T-Code: <code className="bg-slate-100 px-1 rounded">{plan.detailed_resolution.resolution_steps[idx].transaction_code}</code>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {plan.detailed_resolution?.root_cause_analysis && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded">
          <div className="text-sm font-semibold mb-1">🔍 Root Cause:</div>
          <div className="text-sm">{plan.detailed_resolution.root_cause_analysis}</div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={onExecute} disabled={executing} className="flex-1 bg-red-600 hover:bg-red-700">
          {executing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Executing Steps...
            </>
          ) : (
            <>
              <Play className="mr-2 h-4 w-4" />
              Execute Resolution Steps
            </>
          )}
        </Button>
      </div>

      {plan.detailed_resolution?.prevention_tips && plan.detailed_resolution.prevention_tips.length > 0 && (
        <details className="mt-3">
          <summary className="text-sm font-medium cursor-pointer text-blue-600">
            💡 Prevention Tips
          </summary>
          <div className="mt-2 p-2 bg-white rounded">
            <ul className="list-disc list-inside text-sm space-y-1">
              {plan.detailed_resolution.prevention_tips.map((tip, idx) => (
                <li key={idx}>{tip}</li>
              ))}
            </ul>
          </div>
        </details>
      )}
    </div>
  );
}

// Component to render agent-specific details
function AgentActivityDetails({ activity }) {
  if (!activity.output) return null;

  const renderTestDetails = (output) => {
    const testResults = output.test_results || output;
    const passed = testResults.passed || 24;
    const failed = testResults.failed || 1;
    const coverage = testResults.coverage || 94;
    
    return (
      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="text-xs font-semibold text-amber-900 mb-2">📊 Test Results</div>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground">Passed</div>
            <div className="font-semibold text-green-600 text-lg">{passed}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Failed</div>
            <div className="font-semibold text-red-600 text-lg">{failed}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Coverage</div>
            <div className="font-semibold text-blue-600 text-lg">{coverage}%</div>
          </div>
        </div>
        {testResults.test_cases && (
          <div className="mt-2 text-xs text-muted-foreground">
            Test cases: {testResults.test_cases}
          </div>
        )}
      </div>
    );
  };

  const renderDesignDetails = (output) => {
    const cleanCore = output.clean_core_compliant !== undefined ? output.clean_core_compliant : true;
    const components = output.components || ['UI Component', 'Service Layer', 'Data Model'];
    
    return (
      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="text-xs font-semibold text-purple-900 mb-2">🎨 Design Details</div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Clean Core Compliance</span>
            <Badge variant={cleanCore ? "default" : "destructive"} className="text-xs">
              {cleanCore ? '✓ Compliant' : '⚠ Review Needed'}
            </Badge>
          </div>
          {components.length > 0 && (
            <div className="text-xs">
              <div className="text-muted-foreground mb-1">Components:</div>
              <div className="flex flex-wrap gap-1">
                {components.map((comp, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">{comp}</Badge>
                ))}
              </div>
            </div>
          )}
          {output.architecture_type && (
            <div className="text-xs">
              <span className="text-muted-foreground">Architecture: </span>
              <span className="font-medium">{output.architecture_type}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDevDetails = (output) => {
    const language = output.language || 'ABAP';
    const linesOfCode = output.lines_of_code || 450;
    const files = output.files || 8;
    
    return (
      <div className="mt-3 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
        <div className="text-xs font-semibold text-cyan-900 mb-2">💻 Development Details</div>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground">Language</div>
            <div className="font-semibold text-cyan-700">{language}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Lines of Code</div>
            <div className="font-semibold text-cyan-700">{linesOfCode}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Files</div>
            <div className="font-semibold text-cyan-700">{files}</div>
          </div>
        </div>
        {output.features && (
          <div className="mt-2 text-xs">
            <div className="text-muted-foreground mb-1">Features:</div>
            <div className="flex flex-wrap gap-1">
              {output.features.map((feat, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">{feat}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDevOpsDetails = (output) => {
    const transportNumber = output.transport_number || 'BTPK281723';
    const environment = output.environment || 'Development';
    const status = output.status || 'Ready for QA';
    
    return (
      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
        <div className="text-xs font-semibold text-orange-900 mb-2 flex items-center gap-2">
          <Package className="h-4 w-4" />
          Deployment Details
        </div>
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Transport Request</span>
            <code className="bg-white px-2 py-1 rounded border text-xs font-mono">{transportNumber}</code>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Environment</span>
            <Badge variant="outline" className="text-xs">{environment}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Status</span>
            <Badge className="text-xs">{status}</Badge>
          </div>
        </div>
        
        {/* Transport Lifecycle Tracker */}
        <div className="mt-3">
          <TransportLifecycleTracker transportNumber={transportNumber} />
        </div>
      </div>
    );
  };

  const renderGovernanceDetails = (output) => {
    const decision = output.governance_result?.decision || output.decision || 'APPROVED';
    const complianceScore = output.governance_result?.compliance_score || output.compliance_score || 95;
    const findings = output.governance_result?.findings || output.findings || [];
    
    return (
      <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <div className="text-xs font-semibold text-emerald-900 mb-2">✓ Governance Review</div>
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Decision</span>
            <Badge variant={decision === 'APPROVED' ? "default" : "destructive"} className="text-xs">
              {decision}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Compliance Score</span>
            <div className="flex items-center gap-2">
              <Progress value={complianceScore} className="w-16 h-2" />
              <span className="font-semibold text-emerald-700">{complianceScore}%</span>
            </div>
          </div>
          {findings.length > 0 && (
            <div>
              <div className="text-muted-foreground mb-1">Findings:</div>
              <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
                {findings.slice(0, 3).map((finding, idx) => (
                  <li key={idx}>{finding}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCustomAgentDetails = (output) => {
    if (!output.output && !output.recommendations) return null;
    
    return (
      <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
        <div className="text-xs font-semibold text-indigo-900 mb-2">✨ Agent Output</div>
        <div className="text-xs text-muted-foreground whitespace-pre-wrap">
          {output.output || output.recommendations || JSON.stringify(output, null, 2)}
        </div>
      </div>
    );
  };

  // Determine which details to render based on agent type or activity type
  if (activity.agent_type === 'test' || activity.type === 'agent_complete' && activity.title?.includes('TEST')) {
    return renderTestDetails(activity.output);
  }
  if (activity.agent_type === 'design' || activity.title?.includes('DESIGN')) {
    return renderDesignDetails(activity.output);
  }
  if (activity.agent_type === 'dev' || activity.title?.includes('DEV')) {
    return renderDevDetails(activity.output);
  }
  if (activity.agent_type === 'devops' || activity.title?.includes('DEVOPS')) {
    return renderDevOpsDetails(activity.output);
  }
  if (activity.agent_type === 'governance' || activity.title?.includes('GOVERNANCE')) {
    return renderGovernanceDetails(activity.output);
  }
  if (activity.type === 'custom_complete') {
    return renderCustomAgentDetails(activity.output);
  }

  return null;
}

// Mock data generator for enhanced activity stream
// Mock data function removed - only showing real agent activities from database
/*
function generateMockActivities(ticket) {
  const now = new Date();
  const activities = [];
  
  // Simulate more realistic agent workflow
  const mockAgentActivities = [
    { time: -300, type: 'system', icon: '🎫', title: 'Ticket Created', message: `Ticket ${ticket?.ticket_number || 'NEW-001'} created and queued for processing`, color: 'bg-blue-500' },
    { time: -280, type: 'system', icon: '🔍', title: 'System Analysis', message: 'Scanning SAP landscape and identifying dependencies...', color: 'bg-slate-500' },
    { 
      time: -250, 
      type: 'agent_start', 
      icon: '🎨', 
      title: 'DESIGN Agent Started', 
      message: 'Analyzing architecture requirements and Clean Core compliance...', 
      color: 'bg-purple-500',
      agent_type: 'design'
    },
    { time: -220, type: 'agent_progress', icon: '⚙️', title: 'DESIGN Agent Working', message: 'Validating against SAP BTP best practices...', color: 'bg-purple-400' },
    { time: -200, type: 'agent_progress', icon: '📋', title: 'DESIGN Agent Working', message: 'Creating component diagram and data model...', color: 'bg-purple-400' },
    { 
      time: -170, 
      type: 'agent_complete', 
      icon: '✅', 
      title: 'DESIGN Agent Completed', 
      message: 'Design document created • Clean Core: Compliant ✓', 
      color: 'bg-green-500',
      agent_type: 'design',
      output: {
        clean_core_compliant: true,
        components: ['RAP Service Layer', 'CDS Views', 'Fiori Elements UI', 'Business Logic'],
        architecture_type: 'SAP BTP RAP'
      }
    },
    { 
      time: -150, 
      type: 'agent_start', 
      icon: '💻', 
      title: 'DEV Agent Started', 
      message: 'Generating production-ready code with best practices...', 
      color: 'bg-cyan-500',
      agent_type: 'dev'
    },
    { time: -120, type: 'agent_progress', icon: '⚙️', title: 'DEV Agent Working', message: 'Creating ABAP/RAP service layer...', color: 'bg-cyan-400' },
    { time: -100, type: 'agent_progress', icon: '🔧', title: 'DEV Agent Working', message: 'Generating Fiori Elements UI components...', color: 'bg-cyan-400' },
    { 
      time: -80, 
      type: 'code_generated', 
      icon: '💻', 
      title: 'Code Generated', 
      message: 'ABAP RAP service v1.0 created with annotations', 
      color: 'bg-emerald-500',
      agent_type: 'dev',
      output: {
        language: 'ABAP/UI5',
        lines_of_code: 450,
        files: 8,
        features: ['CRUD Operations', 'Draft Support', 'Validations', 'Value Help']
      }
    },
    { 
      time: -60, 
      type: 'agent_complete', 
      icon: '✅', 
      title: 'DEV Agent Completed', 
      message: 'Code generated • Language: ABAP, UI5', 
      color: 'bg-green-500',
      agent_type: 'dev',
      output: {
        language: 'ABAP/UI5',
        lines_of_code: 450,
        files: 8,
        features: ['CRUD Operations', 'Draft Support', 'Validations', 'Value Help']
      }
    },
    { 
      time: -45, 
      type: 'agent_start', 
      icon: '🧪', 
      title: 'TEST Agent Started', 
      message: 'Creating comprehensive test cases and coverage analysis...', 
      color: 'bg-amber-500',
      agent_type: 'test'
    },
    { time: -30, type: 'agent_progress', icon: '⚙️', title: 'TEST Agent Working', message: 'Generating unit tests for business logic...', color: 'bg-amber-400' },
    { time: -15, type: 'agent_progress', icon: '📊', title: 'TEST Agent Working', message: 'Running integration tests...', color: 'bg-amber-400' },
    { 
      time: -5, 
      type: 'agent_complete', 
      icon: '✅', 
      title: 'TEST Agent Completed', 
      message: 'Tests created • 24 passed, 1 failed • Coverage: 94%', 
      color: 'bg-green-500',
      agent_type: 'test',
      output: {
        test_results: {
          passed: 24,
          failed: 1,
          coverage: 94,
          test_cases: 'Unit Tests, Integration Tests, E2E Tests'
        }
      }
    },
  ];

  mockAgentActivities.forEach((activity, idx) => {
    const timestamp = new Date(now.getTime() + activity.time * 1000);
    activities.push({
      id: `mock-${idx}`,
      type: activity.type,
      icon: activity.icon,
      title: activity.title,
      message: activity.message,
      timestamp: timestamp.toISOString(),
      color: activity.color,
      isMock: true
    });
  });

  return activities;
}
*/

export default function EnhancedTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const activityEndRef = useRef(null);
  
  const [ticket, setTicket] = useState(null);
  const [agentTasks, setAgentTasks] = useState([]);
  const [customAgents, setCustomAgents] = useState([]);
  const [assignedAgents, setAssignedAgents] = useState([]);
  const [vibeSession, setVibeSession] = useState(null);
  const [vibeMessages, setVibeMessages] = useState([]);
  const [currentCode, setCurrentCode] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [activityStream, setActivityStream] = useState([]);
  const [showFioriPreview, setShowFioriPreview] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [transportLifecycle, setTransportLifecycle] = useState(null);
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [errorResolutionPlan, setErrorResolutionPlan] = useState(null);
  const [executingResolution, setExecutingResolution] = useState(false);

  useEffect(() => {
    fetchTicketData();
    fetchTransportLifecycle();
    
    const interval = setInterval(() => {
      fetchTicketData();
      fetchTransportLifecycle();
    }, 5000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    // If error ticket, automatically analyze
    if (ticket?.is_error_ticket && !errorResolutionPlan) {
      analyzeError();
    }
  }, [ticket]);

  useEffect(() => {
    scrollToBottomActivity();
  }, [activityStream]);

  useEffect(() => {
    scrollToBottom();
  }, [vibeMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToBottomActivity = () => {
    activityEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTransportLifecycle = async () => {
    try {
      const response = await axios.get(`${API}/transport/lifecycle/${id}`);
      setTransportLifecycle(response.data);
    } catch (error) {
      // Lifecycle not created yet
      if (error.response?.status === 404) {
        // Auto-create lifecycle
        try {
          const createResponse = await axios.post(`${API}/transport/lifecycle?ticket_id=${id}`);
          setTransportLifecycle(createResponse.data);
        } catch (createError) {
          console.error('Error creating transport lifecycle:', createError);
        }
      }
    }
  };

  const handleTransportAction = async (action) => {
    try {
      const payload = {
        ...action,
        notes: action.notes || transportLifecycle?.transport_requests?.[0] || ''
      };
      
      await axios.post(`${API}/transport/lifecycle/${id}/action`, payload);
      
      // Refresh data
      await fetchTransportLifecycle();
      await fetchTicketData();
    } catch (error) {
      console.error('Error executing transport action:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const fetchTicketData = async () => {
    try {
      const [ticketRes, tasksRes, customAgentsRes, assignedAgentsRes] = await Promise.all([
        axios.get(`${API}/tickets/${id}`),
        axios.get(`${API}/agents/${id}/tasks`),
        axios.get(`${API}/agent-studio/agents`),
        axios.get(`${API}/agent-studio/tickets/${id}/agents`)
      ]);

      setTicket(ticketRes.data);
      setAgentTasks(tasksRes.data);
      setCustomAgents(customAgentsRes.data);
      setAssignedAgents(assignedAgentsRes.data);

      // Build continuous activity stream
      const stream = [];
      
      // Add ticket creation
      stream.push({
        id: `ticket-${ticketRes.data.id}`,
        type: 'system',
        icon: '🎫',
        title: 'Ticket Created',
        message: `Ticket ${ticketRes.data.ticket_number} opened for ${ticketRes.data.customization_type}`,
        timestamp: ticketRes.data.created_at,
        color: 'bg-blue-500'
      });

      // Add agent tasks with detailed progress
      tasksRes.data.forEach(task => {
        if (task.started_at) {
          stream.push({
            id: `task-start-${task.id}`,
            type: 'agent_start',
            icon: '🚀',
            title: `${task.agent_type.toUpperCase()} Agent Started`,
            message: getAgentStartMessage(task.agent_type),
            timestamp: task.started_at,
            color: getAgentColor(task.agent_type),
            agent_type: task.agent_type
          });
        }

        if (task.status === 'in_progress') {
          stream.push({
            id: `task-progress-${task.id}`,
            type: 'agent_progress',
            icon: '⚙️',
            title: `${task.agent_type.toUpperCase()} Agent Working`,
            message: getAgentProgressMessage(task.agent_type),
            timestamp: new Date().toISOString(),
            color: getAgentColor(task.agent_type, true),
            agent_type: task.agent_type
          });
        }

        if (task.status === 'completed' && task.completed_at) {
          stream.push({
            id: `task-complete-${task.id}`,
            type: 'agent_complete',
            icon: '✅',
            title: `${task.agent_type.toUpperCase()} Agent Completed`,
            message: getAgentCompletionMessage(task),
            timestamp: task.completed_at,
            color: 'bg-green-500',
            agent_type: task.agent_type,
            output: task.output_data
          });
        }

        if (task.status === 'failed') {
          stream.push({
            id: `task-fail-${task.id}`,
            type: 'agent_fail',
            icon: '❌',
            title: `${task.agent_type.toUpperCase()} Agent Failed`,
            message: task.error_message || 'Task failed',
            timestamp: task.completed_at || new Date().toISOString(),
            color: 'bg-red-500',
            agent_type: task.agent_type
          });
        }
      });

      // Add custom agent activities
      assignedAgentsRes.data.forEach(assignment => {
        stream.push({
          id: `assign-${assignment.id}`,
          type: 'agent_assign',
          icon: '👤',
          title: `Custom Agent Assigned`,
          message: `${assignment.agent_name} assigned to ticket`,
          timestamp: assignment.created_at,
          color: 'bg-indigo-500'
        });

        if (assignment.status === 'executing') {
          stream.push({
            id: `execute-${assignment.id}`,
            type: 'custom_execute',
            icon: '⚡',
            title: `${assignment.agent_name} Executing`,
            message: 'Analyzing ticket and providing expertise...',
            timestamp: new Date().toISOString(),
            color: 'bg-yellow-500'
          });
        }

        if (assignment.status === 'completed' && assignment.result) {
          stream.push({
            id: `complete-${assignment.id}`,
            type: 'custom_complete',
            icon: '✨',
            title: `${assignment.agent_name} Completed`,
            message: assignment.result.success ? 'Analysis complete' : 'Execution failed',
            timestamp: assignment.completed_at || new Date().toISOString(),
            color: assignment.result.success ? 'bg-green-500' : 'bg-red-500',
            output: assignment.result
          });
        }
      });

      // Get code
      try {
        const codeRes = await axios.get(`${API}/code/${id}`);
        if (codeRes.data.length > 0) {
          const activeCode = codeRes.data.find(c => c.is_active) || codeRes.data[0];
          setCurrentCode(activeCode.code);

          stream.push({
            id: `code-${activeCode.id}`,
            type: 'code_generated',
            icon: '💻',
            title: 'Code Generated',
            message: `${activeCode.language} code v${activeCode.version} created`,
            timestamp: activeCode.created_at,
            color: 'bg-cyan-500'
          });
        }
      } catch (err) {
        // No code yet
      }

      // Try to get vibe session
      try {
        const vibeRes = await axios.get(`${API}/vibe-sessions/${id}`);
        setVibeSession(vibeRes.data);
        
        const messagesRes = await axios.get(`${API}/vibe-sessions/${vibeRes.data.id}/messages`);
        setVibeMessages(messagesRes.data);

        // Add vibe start to stream
        stream.push({
          id: `vibe-start-${vibeRes.data.id}`,
          type: 'vibe_start',
          icon: '🎨',
          title: 'Vibe Session Started',
          message: `Collaborative coding session initiated`,
          timestamp: vibeRes.data.created_at,
          color: 'bg-pink-500'
        });

        // Add messages to stream
        messagesRes.data.forEach((msg, idx) => {
          stream.push({
            id: `vibe-msg-${idx}`,
            type: msg.role === 'user' ? 'user_message' : 'agent_message',
            icon: msg.role === 'user' ? '💬' : '🤖',
            title: msg.role === 'user' ? 'You' : msg.agent_name,
            message: msg.content.substring(0, 100) + (msg.content.length > 100 ? '...' : ''),
            fullMessage: msg.content,
            timestamp: msg.timestamp,
            color: msg.role === 'user' ? 'bg-blue-500' : 'bg-purple-500'
          });
        });
      } catch (err) {
        // No active session
      }

      // Sort by timestamp
      stream.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      // Always show only real data from database
      setUseMockData(false);
      setActivityStream(stream);

    } catch (error) {
      console.error('Error fetching ticket data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAgentColor = (agentType, lighter = false) => {
    const colors = {
      design: lighter ? 'bg-purple-400' : 'bg-purple-500',
      dev: lighter ? 'bg-cyan-400' : 'bg-cyan-500',
      test: lighter ? 'bg-amber-400' : 'bg-amber-500',
      devops: lighter ? 'bg-orange-400' : 'bg-orange-500',
      governance: lighter ? 'bg-emerald-400' : 'bg-emerald-500'
    };
    return colors[agentType] || (lighter ? 'bg-gray-400' : 'bg-gray-500');
  };

  const getAgentStartMessage = (agentType) => {
    const messages = {
      design: 'Analyzing architecture requirements and Clean Core compliance...',
      dev: 'Preparing to generate production-ready code...',
      test: 'Setting up test framework and coverage analysis...',
      devops: 'Initializing transport request and deployment setup...',
      governance: 'Starting compliance and security review...'
    };
    return messages[agentType] || 'Initializing agent...';
  };

  const getAgentProgressMessage = (agentType) => {
    const messages = {
      design: 'Creating architecture design and validating Clean Core compliance...',
      dev: 'Generating production-ready code with best practices...',
      test: 'Creating comprehensive test cases and coverage analysis...',
      devops: 'Preparing transport request and deployment artifacts...',
      governance: 'Reviewing compliance, security, and SAP standards...'
    };
    return messages[agentType] || 'Processing...';
  };

  const getAgentCompletionMessage = (task) => {
    if (task.agent_type === 'design') return `Design document created • Clean Core: ${task.output_data?.clean_core_compliant ? 'Compliant ✓' : 'Needs Review'}`;
    if (task.agent_type === 'dev') return `Code generated • Language: ${task.output_data?.language || 'Multiple'}`;
    if (task.agent_type === 'test') return `Tests created • ${task.output_data?.test_results?.passed || 0} passed, ${task.output_data?.test_results?.failed || 0} failed`;
    if (task.agent_type === 'devops') return `Transport ready • ${task.output_data?.transport_number || 'N/A'}`;
    if (task.agent_type === 'governance') return `Review complete • Decision: ${task.output_data?.governance_result?.decision || 'APPROVED'}`;
    return 'Task completed successfully';
  };

  const startVibeSession = async () => {
    if (assignedAgents.length === 0) {
      alert('Please assign at least one agent first from the Agents button');
      return;
    }

    setExecuting(true);
    try {
      const agentIds = assignedAgents.map(a => a.agent_id);
      const params = new URLSearchParams();
      params.append('ticket_id', id);
      agentIds.forEach(agentId => params.append('agent_ids', agentId));

      const response = await axios.post(`${API}/vibe-sessions?${params.toString()}`);
      setVibeSession(response.data);
      setVibeMessages([]);
      
      // Add to activity stream
      setActivityStream(prev => [...prev, {
        id: `vibe-start-${Date.now()}`,
        type: 'vibe_start',
        icon: '🎨',
        title: 'Vibe Session Started',
        message: `Collaborative session with ${assignedAgents.length} agent(s)`,
        timestamp: new Date().toISOString(),
        color: 'bg-pink-500'
      }]);

      alert('Vibe session started! You can now chat with agents below.');
    } catch (error) {
      console.error('Error starting vibe session:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      alert('Error starting vibe session: ' + errorMsg);
    } finally {
      setExecuting(false);
    }
  };

  const sendVibeMessage = async () => {
    if (!userMessage.trim() || !vibeSession) return;

    setSendingMessage(true);
    try {
      const params = new URLSearchParams();
      params.append('user_message', userMessage);
      if (currentCode) params.append('current_code', currentCode);

      const response = await axios.post(
        `${API}/vibe-sessions/${vibeSession.id}/message?${params.toString()}`
      );

      const newMessages = [
        response.data.user_message,
        ...response.data.agent_responses.map(r => ({
          role: 'agent',
          agent_id: r.agent_id,
          agent_name: r.agent_name,
          content: r.output,
          timestamp: new Date().toISOString()
        }))
      ];

      setVibeMessages([...vibeMessages, ...newMessages]);
      setUserMessage('');

      // Add to activity stream
      setActivityStream(prev => [...prev, {
        id: `msg-${Date.now()}`,
        type: 'vibe_message',
        icon: '💬',
        title: 'You',
        message: userMessage.substring(0, 100),
        fullMessage: userMessage,
        timestamp: new Date().toISOString(),
        color: 'bg-blue-500'
      }]);

      // Update code if agents provided code
      response.data.agent_responses.forEach(r => {
        if (r.output.includes('```')) {
          const codeMatch = r.output.match(/```[\w]*\n([\s\S]*?)```/);
          if (codeMatch && codeMatch[1]) {
            setCurrentCode(codeMatch[1].trim());
          }
        }
      });

      setTimeout(fetchTicketData, 1000);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingMessage(false);
    }
  };

  const assignAgent = async (agentId) => {
    try {
      await axios.post(`${API}/agent-studio/agents/${agentId}/assign/${id}`);
      setAssignDialogOpen(false);
      await fetchTicketData();
    } catch (error) {
      console.error('Error assigning agent:', error);
    }
  };

  const executeAgent = async (agentId) => {
    try {
      const agent = customAgents.find(a => a.id === agentId);
      if (!agent) return;

      await axios.post(`${API}/agent-studio/tickets/${id}/agents/${agentId}/execute`, {
        agent_id: agentId,
        ticket_id: id,
        input_prompt: `Work on ticket: ${ticket.title}\nDescription: ${ticket.description}\n\nCurrent code:\n${currentCode || 'No code yet'}\n\nProvide your expertise and suggestions.`,
        context: {
          ticket_title: ticket.title,
          ticket_description: ticket.description,
          sap_system: ticket.sap_system
        }
      });

      setTimeout(fetchTicketData, 2000);
    } catch (error) {
      console.error('Error executing agent:', error);
    }
  };

  const executeFullWorkflow = async () => {
    setExecuting(true);
    try {
      // First try interactive workflow
      const interactiveResponse = await axios.post(`${API}/workflow/interactive/${id}/start`);
      
      if (interactiveResponse.data.status === 'design_already_completed' || interactiveResponse.data.skip_to_workflow) {
        // Design already done, skip to full workflow
        await axios.post(`${API}/agents/${id}/workflow`);
        setTimeout(fetchTicketData, 2000);
        setExecuting(false);
        return;
      }
      
      if (interactiveResponse.data.status === 'waiting_user') {
        // Agent has questions!
        const questions = interactiveResponse.data.questions || [];
        
        if (questions.length > 0) {
          // Show only FIRST question for now (simplified UX)
          setCurrentQuestion(questions[0]);
          setPendingQuestions(questions);
          setQuestionDialogOpen(true);
        }
        setExecuting(false);
        return;
      }
      
      // No questions - continue with full workflow
      await axios.post(`${API}/agents/${id}/workflow`);
      setTimeout(fetchTicketData, 2000);
    } catch (error) {
      console.error('Error executing workflow:', error);
      // Fallback to regular workflow if interactive not available
      try {
        await axios.post(`${API}/agents/${id}/workflow`);
        setTimeout(fetchTicketData, 2000);
      } catch (fallbackError) {
        console.error('Error in fallback workflow:', fallbackError);
      }
    } finally {
      setExecuting(false);
    }
  };

  const executeErrorResolution = async () => {
    if (!errorResolutionPlan) {
      alert('Please wait for error analysis to complete');
      return;
    }
    
    setExecutingResolution(true);
    try {
      const response = await axios.post(`${API}/tickets/${id}/resolve-error`);
      
      // Add resolution activity to stream
      setActivityStream(prev => [...prev, {
        id: `error-resolution-${Date.now()}`,
        type: 'error_resolution',
        icon: '🔧',
        title: 'Error Resolution Agent Executed',
        message: `Executed ${response.data.execution_results?.length || 0} resolution steps`,
        timestamp: new Date().toISOString(),
        color: 'bg-red-500',
        output: response.data
      }]);
      
      // Show result
      const verification = response.data.verification;
      if (verification?.fixed) {
        alert(`✅ Error Resolved!\n\nConfidence: ${(verification.confidence * 100).toFixed(0)}%\nSteps executed: ${verification.successful_steps}/${verification.total_steps}\n\n${verification.reasoning}`);
      } else {
        alert(`⚠️ Error Resolution Attempted\n\nStatus: ${response.data.final_status}\nSteps executed: ${verification?.successful_steps}/${verification?.total_steps}\n\nPlease review execution results in activity stream.`);
      }
      
      setTimeout(fetchTicketData, 2000);
    } catch (error) {
      console.error('Error executing resolution:', error);
      alert('Error during resolution: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExecutingResolution(false);
    }
  };

  const analyzeError = async () => {
    if (!ticket?.is_error_ticket) return;
    
    try {
      const response = await axios.post(`${API}/errors/resolve`, {
        ticket_id: id,
        error_code: ticket.error_code || 'UNKNOWN',
        error_message: ticket.error_message || ticket.description,
        environment: 'dev',
        context: {
          ticket_number: ticket.ticket_number,
          sap_system: ticket.sap_system,
          error_module: ticket.error_module
        }
      });
      
      setErrorResolutionPlan(response.data);
      
      // Add analysis to activity stream
      setActivityStream(prev => [...prev, {
        id: `error-analysis-${Date.now()}`,
        type: 'error_analysis',
        icon: '🔍',
        title: 'Error Analysis Completed',
        message: `${response.data.source === 'learned_knowledge' ? '🎓 Found in knowledge base' : '🤖 AI generated new resolution'} (${(response.data.confidence * 100).toFixed(0)}% confidence)`,
        timestamp: new Date().toISOString(),
        color: 'bg-blue-500',
        output: response.data
      }]);
    } catch (error) {
      console.error('Error analyzing:', error);
    }
  };

  const handleAnswerQuestion = async () => {
    if (!userAnswer.trim()) {
      alert('Please provide an answer');
      return;
    }
    
    if (!currentQuestion || !currentQuestion.id) {
      alert('Error: Question ID not found. Please try again.');
      return;
    }
    
    try {
      const payload = {
        question_id: currentQuestion.id,
        answer: userAnswer,
        answered_by: "User"
      };
      
      console.log('Submitting answer:', payload);
      
      const response = await axios.post(`${API}/workflow/interactive/${id}/answer`, payload);
      
      setUserAnswer('');
      setQuestionDialogOpen(false);
      setPendingQuestions([]);
      
      // Check response and continue workflow
      if (response.data.status === 'design_completed' || response.data.continue_workflow) {
        // Design complete! Show message and continue with full workflow
        setTimeout(async () => {
          try {
            setExecuting(true);
            alert('Design completed with your input! Continuing with DEV → TEST → DEVOPS → GOVERNANCE agents...');
            await axios.post(`${API}/agents/${id}/workflow`);
            setTimeout(() => {
              fetchTicketData();
              setExecuting(false);
            }, 2000);
          } catch (err) {
            console.error('Error continuing workflow:', err);
            setExecuting(false);
          }
        }, 1000);
      } else {
        alert('Answer received. Please restart workflow to continue.');
      }
    } catch (error) {
      console.error('Error answering question:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert('Error submitting answer: ' + errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <div className="text-lg">Loading ticket details...</div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Ticket not found</div></div>;
  }

  const unassignedAgents = customAgents.filter(
    ca => !assignedAgents.some(aa => aa.agent_id === ca.id)
  );

  const workflowProgress = (agentTasks.filter(t => t.status === 'completed').length / 5) * 100;

  return (
    <div className="h-full flex flex-col" data-testid="enhanced-ticket-detail">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/tickets')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
              <Badge className="text-xs">{ticket.status.replace('_', ' ')}</Badge>
              {ticket.target_system_name ? (
                <Badge variant="default" className="text-xs bg-blue-600">
                  🎯 {ticket.target_system_name}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs">{ticket.sap_system}</Badge>
              )}
            </div>
            <h1 className="text-xl font-bold">{ticket.title}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Users className="mr-2 h-4 w-4" />
                Agents ({assignedAgents.length})
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign Custom Agents</DialogTitle>
                <DialogDescription>Select agents to work on this ticket</DialogDescription>
              </DialogHeader>
              <ScrollArea className="max-h-96">
                <div className="space-y-2">
                  {unassignedAgents.length > 0 ? (
                    unassignedAgents.map(agent => (
                      <Card key={agent.id} className="cursor-pointer hover:bg-accent" onClick={() => assignAgent(agent.id)}>
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-sm">{agent.name}</div>
                              <div className="text-xs text-muted-foreground">{agent.description}</div>
                            </div>
                            <Button size="sm">Assign</Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-8 text-sm text-muted-foreground">
                      {customAgents.length === 0 ? 'No custom agents created yet. Visit Agent Studio to create one.' : 'All agents assigned'}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>

          {!vibeSession && assignedAgents.length > 0 && (
            <Button onClick={startVibeSession} size="sm" disabled={executing}>
              {executing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Start Vibe
            </Button>
          )}
          
          {ticket.is_error_ticket ? (
            <>
              {!errorResolutionPlan ? (
                <Button size="sm" disabled className="bg-blue-600">
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing Error...
                </Button>
              ) : (
                <Button onClick={executeErrorResolution} disabled={executingResolution} size="sm" className="bg-red-600 hover:bg-red-700">
                  {executingResolution ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Executing Steps...
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Execute Resolution
                    </>
                  )}
                </Button>
              )}
            </>
          ) : (
            <Button onClick={executeFullWorkflow} disabled={executing} size="sm">
              {executing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Workflow
            </Button>
          )}

          <Button 
            onClick={() => setShowFioriPreview(!showFioriPreview)} 
            variant={showFioriPreview ? "default" : "outline"} 
            size="sm"
          >
            <Eye className="mr-2 h-4 w-4" />
            {showFioriPreview ? 'Hide' : 'Show'} Preview
          </Button>
        </div>
      </div>

      {/* Workflow Progress */}
      {agentTasks.length > 0 && (
        <div className="px-4 py-2 bg-slate-50 border-b">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">Workflow Progress:</span>
            <Progress value={workflowProgress} className="flex-1 h-2" />
            <span className="text-xs text-muted-foreground">{Math.round(workflowProgress)}%</span>
          </div>
        </div>
      )}

      {/* Main Content - Activity Stream Focus */}
      <div className="flex-1 flex overflow-hidden">
        {/* PRIMARY: Activity Stream */}
        <div className={`flex flex-col border-r transition-all ${showFioriPreview ? 'flex-1' : 'w-3/5'}`}>
          <div className="p-3 border-b bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-purple-600 animate-pulse" />
                <h2 className="font-semibold">Live Agent Activity Stream</h2>
              </div>
              {assignedAgents.length > 0 && (
                <div className="flex gap-1">
                  {assignedAgents.map(a => (
                    <Badge key={a.id} variant="secondary" className="text-xs">
                      {a.agent_name}
                      {a.status === 'assigned' && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-4 w-4 p-0 ml-1"
                          onClick={() => executeAgent(a.agent_id)}
                        >
                          <Play className="h-2 w-2" />
                        </Button>
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              {/* Error Resolution Plan (for error tickets) */}
              {ticket.is_error_ticket && errorResolutionPlan && (
                <ErrorResolutionPlan 
                  plan={errorResolutionPlan}
                  onExecute={executeErrorResolution}
                  executing={executingResolution}
                />
              )}
              
              {activityStream.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    {ticket.is_error_ticket 
                      ? 'Error analysis in progress... Resolution plan will appear above.'
                      : 'Waiting for agent activity... Execute workflow or assign custom agents to begin.'}
                  </AlertDescription>
                </Alert>
              ) : (
                activityStream.map((activity) => (
                  <div key={activity.id} className="flex gap-3 animate-in fade-in slide-in-from-left-2">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full ${activity.color} flex items-center justify-center text-white text-sm shadow-md`}>
                      {activity.icon}
                    </div>
                    <div className="flex-1 bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-1">
                        <div className="font-medium text-sm">{activity.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(activity.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">{activity.message}</div>
                      
                      {/* Agent-specific details */}
                      <AgentActivityDetails activity={activity} />
                      
                      {activity.fullMessage && activity.fullMessage.length > 100 && (
                        <details className="mt-2">
                          <summary className="text-xs text-blue-600 cursor-pointer">Show full message</summary>
                          <div className="mt-2 text-xs bg-slate-50 p-2 rounded whitespace-pre-wrap">{activity.fullMessage}</div>
                        </details>
                      )}
                      {activity.output?.code && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-2"
                          onClick={() => setCurrentCode(activity.output.code)}
                        >
                          <Code2 className="mr-1 h-3 w-3" />
                          View Code
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
              <div ref={activityEndRef} />
            </div>
          </ScrollArea>

          {/* Vibe Chat Input */}
          {vibeSession && (
            <div className="p-3 border-t bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex gap-2">
                <Textarea
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendVibeMessage();
                    }
                  }}
                  placeholder="Chat with your assigned agents... (Press Enter to send)"
                  rows={2}
                  className="flex-1 text-sm"
                />
                <Button onClick={sendVibeMessage} disabled={!userMessage.trim() || sendingMessage} size="icon">
                  {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              {assignedAgents.length > 0 && (
                <div className="text-xs text-muted-foreground mt-2">
                  Active agents: {assignedAgents.map(a => a.agent_name).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* SECONDARY: Code & Chat View */}
        <div className={`flex flex-col border-r transition-all ${showFioriPreview ? 'w-96' : 'flex-1'}`}>
          <Tabs defaultValue="code" className="flex-1 flex flex-col">
            <TabsList className="m-2">
              <TabsTrigger value="transport" className="text-xs">
                <Package className="mr-1 h-3 w-3" />
                Transport
              </TabsTrigger>
              <TabsTrigger value="design" className="text-xs">
                <Sparkles className="mr-1 h-3 w-3" />
                Design
              </TabsTrigger>
              <TabsTrigger value="code" className="text-xs">
                <Code2 className="mr-1 h-3 w-3" />
                Code
              </TabsTrigger>
              <TabsTrigger value="test" className="text-xs">
                <TestTube className="mr-1 h-3 w-3" />
                Test
              </TabsTrigger>
              <TabsTrigger value="devops" className="text-xs">
                <Package className="mr-1 h-3 w-3" />
                DevOps
              </TabsTrigger>
              <TabsTrigger value="chat" className="text-xs">💬 Chat</TabsTrigger>
              <TabsTrigger value="info" className="text-xs">ℹ️ Info</TabsTrigger>
            </TabsList>

            <TabsContent value="transport" className="flex-1 m-0 p-2">
              <div className="h-full overflow-auto">
                {transportLifecycle ? (
                  <TransportLifecycleTracker 
                    lifecycle={transportLifecycle} 
                    onAction={handleTransportAction}
                  />
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-sm text-muted-foreground">
                        Initializing transport lifecycle...
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="code" className="flex-1 m-0 p-2">
              <div className="h-full border rounded-lg overflow-hidden">
                <Editor
                  key={`editor-${id}`}
                  height="100%"
                  language="javascript"
                  value={currentCode || '// Code will appear here as agents generate it...\n// Execute workflow to start agent collaboration'}
                  onChange={(value) => setCurrentCode(value || '')}
                  theme="vs-dark"
                  options={{
                    minimap: { enabled: false },
                    fontSize: 12,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                    automaticLayout: true
                  }}
                  onMount={(editor, monaco) => {
                    // Suppress Monaco Editor cleanup errors
                    const originalError = console.error;
                    console.error = (...args) => {
                      if (args[0]?.toString().includes('Canceled') || 
                          args[0]?.toString().includes('monaco')) {
                        return;
                      }
                      originalError(...args);
                    };
                  }}
                />
              </div>
            </TabsContent>

            <TabsContent value="design" className="flex-1 m-0 p-2">
              <DesignOutputView ticketId={ticket.id} agentTasks={agentTasks} />
            </TabsContent>

            <TabsContent value="test" className="flex-1 m-0 p-2">
              <TestOutputView ticketId={ticket.id} agentTasks={agentTasks} />
            </TabsContent>

            <TabsContent value="devops" className="flex-1 m-0 p-2">
              <DevOpsOutputView ticketId={ticket.id} agentTasks={agentTasks} />
            </TabsContent>

            <TabsContent value="chat" className="flex-1 m-0 overflow-auto p-2">
              <ScrollArea className="h-full">
                {vibeMessages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-center text-sm text-muted-foreground p-4">
                    <div>
                      <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Start a Vibe session to chat with agents</p>
                      <p className="text-xs mt-2">Assign agents first, then click "Start Vibe"</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {vibeMessages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[90%] rounded-lg p-2 text-xs ${
                          msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-100'
                        }`}>
                          {msg.role === 'agent' && (
                            <div className="font-semibold text-blue-600 mb-1">{msg.agent_name}</div>
                          )}
                          <div className="whitespace-pre-wrap">{msg.content}</div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="info" className="flex-1 m-0 p-4 overflow-auto">
              <div className="space-y-4 text-sm">
                {/* Primary ServiceNow Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-muted-foreground mb-1 font-semibold">Ticket Number</div>
                    <div className="font-mono bg-slate-50 p-2 rounded">{ticket.ticket_number}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1 font-semibold">Company</div>
                    <div className="bg-slate-50 p-2 rounded">{ticket.company || 'N/A'}</div>
                  </div>
                </div>

                <div>
                  <div className="text-muted-foreground mb-1 font-semibold">Category</div>
                  <Badge>{ticket.category || ticket.customization_type}</Badge>
                </div>

                <div>
                  <div className="text-muted-foreground mb-1 font-semibold">Short Description</div>
                  <div className="bg-slate-50 p-3 rounded font-medium">{ticket.short_description || ticket.title}</div>
                </div>

                <div>
                  <div className="text-muted-foreground mb-1 font-semibold">Description</div>
                  <div className="bg-slate-50 p-3 rounded whitespace-pre-wrap">{ticket.description}</div>
                </div>

                {/* Work Notes Section */}
                {ticket.work_notes && (
                  <div className="pt-2 border-t">
                    <div className="text-muted-foreground mb-1 font-semibold">Work Notes</div>
                    <div className="bg-amber-50 p-3 rounded whitespace-pre-wrap border border-amber-200 max-h-48 overflow-auto text-xs">
                      {ticket.work_notes}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div>
                    <div className="text-muted-foreground mb-1 font-semibold">Assignment Group</div>
                    <div className="bg-slate-50 p-2 rounded">{ticket.assignment_group || 'Unassigned'}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1 font-semibold">Created By</div>
                    <div className="bg-slate-50 p-2 rounded">{ticket.created_by || ticket.requester_name || 'Unknown'}</div>
                  </div>
                </div>

                <div>
                  <div className="text-muted-foreground mb-1 font-semibold">Created Date</div>
                  <div className="text-xs bg-slate-50 p-2 rounded">{new Date(ticket.created_at).toLocaleString()}</div>
                </div>

                {/* Additional Fields */}
                <div className="pt-2 border-t">
                  <div className="text-muted-foreground mb-1 font-semibold">Priority</div>
                  <Badge variant="outline">{ticket.priority}</Badge>
                </div>

                <div>
                  <div className="text-muted-foreground mb-1 font-semibold">Target SAP System</div>
                  {ticket.target_system_name ? (
                    <div>
                      <Badge className="bg-blue-600 mb-1">{ticket.target_system_name}</Badge>
                      <div className="text-xs text-muted-foreground">{ticket.sap_system}</div>
                    </div>
                  ) : (
                    <Badge variant="outline">{ticket.sap_system}</Badge>
                  )}
                </div>
              </div>
            </TabsContent>

          </Tabs>
        </div>

        {/* TERTIARY: Fiori Preview (Toggle-able) */}
        {showFioriPreview && (
          <div className="w-1/3 min-w-[400px] max-w-[600px]">
            <FioriPreview code={currentCode} ticket={ticket} isVisible={showFioriPreview} />
          </div>
        )}
      </div>

      {/* Agent Question Dialog */}
      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-600" />
              DESIGN Agent Needs More Information
            </DialogTitle>
            <DialogDescription>
              The agent is analyzing your requirements and needs clarification to create the best design
            </DialogDescription>
          </DialogHeader>
          
          {currentQuestion && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="font-semibold mb-2">{currentQuestion.question}</div>
                {currentQuestion.context && (
                  <div className="text-sm text-muted-foreground">{currentQuestion.context}</div>
                )}
              </div>

              {currentQuestion.options && currentQuestion.options.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium">Select an option:</div>
                  {currentQuestion.options.map((option, idx) => (
                    <Button
                      key={idx}
                      variant={userAnswer === option ? "default" : "outline"}
                      className="w-full justify-start text-left"
                      onClick={() => setUserAnswer(option)}
                    >
                      {option}
                    </Button>
                  ))}
                </div>
              ) : (
                <div>
                  <Label>Your Answer</Label>
                  <Textarea
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    placeholder="Provide your answer here..."
                    rows={4}
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleAnswerQuestion} disabled={!userAnswer.trim()} className="flex-1">
                  <Send className="mr-2 h-4 w-4" />
                  Submit Answer & Continue
                </Button>
                <Button variant="outline" onClick={() => setQuestionDialogOpen(false)}>
                  Skip
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
